/**
 * Общие детали страницы «Настройки»: лист-секция, блок, поле, поле секрета, карточка ключа,
 * черновик формы (только изменённые значения), строка сохранения, учёт несохранённых правок.
 */
import { createContext, useContext, useEffect, useId, useState, type FormEvent, type ReactNode } from "react";
import { CircleAlert, CircleCheck, Eye, EyeOff, KeyRound, Lock } from "lucide-react";
import { errText } from "../lib/api";

/** Переменные .env, которые перекрывают настройки (сервер: services/settings.ts → ENV_MAP). */
export const SET_ENV_NAME: Record<string, string> = {
  gemini_key: "GEMINI_API_KEY",
  vk_group_token: "VK_GROUP_TOKEN",
  vk_group_id: "VK_GROUP_ID",
  vk_client_id: "VK_CLIENT_ID",
  gemini_model: "GEMINI_MODEL",
  claude_model: "CLAUDE_MODEL",
};

export const setEnvHint = (key: string) => `Задано в .env (${SET_ENV_NAME[key] ?? key})`;

/* ================= Несохранённые правки ================= */
type DirtyReport = (id: string, dirty: boolean) => void;
export const SetDirtyCtx = createContext<DirtyReport>(() => {});

/** Сообщает странице, что в части есть несохранённые правки (для подтверждения ухода). */
export function useSetDirty(id: string, dirty: boolean) {
  const report = useContext(SetDirtyCtx);
  useEffect(() => {
    report(id, dirty);
  }, [report, id, dirty]);
  useEffect(() => () => report(id, false), [report, id]);
}

/* ================= Черновик формы ================= */
/** Храним только изменённые поля: остальное всегда берётся из свежих данных сервера. */
export function useSetDraft<T extends Record<string, unknown>>(source: T) {
  const [patch, setPatch] = useState<Partial<T>>({});
  const value = { ...source, ...patch } as T;
  const changedKeys = (Object.keys(patch) as (keyof T)[]).filter((k) => patch[k] !== source[k]);
  const changes = Object.fromEntries(changedKeys.map((k) => [k, patch[k]])) as Partial<T>;
  return {
    value,
    changes,
    dirty: changedKeys.length > 0,
    set: <K extends keyof T>(key: K, v: T[K]) => setPatch((p) => ({ ...p, [key]: v })),
    reset: () => setPatch({}),
  };
}

/* ================= Раскладка ================= */
export function SetSection({ id, title, status, children }: { id: string; title: string; status?: ReactNode; children: ReactNode }) {
  return (
    <section id={id} className="sheet set-sec" aria-labelledby={`${id}-title`}>
      <header className="set-sec__head">
        <h2 id={`${id}-title`}>{title}</h2>
        {status && <div className="set-sec__status">{status}</div>}
      </header>
      {children}
    </section>
  );
}

export function SetBlock({
  title,
  aside,
  children,
  className = "",
}: {
  title?: ReactNode;
  aside?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={`set-block ${className}`}>
      {(title || aside) && (
        <div className="set-block__head">
          {title && <h3>{title}</h3>}
          {aside}
        </div>
      )}
      {children}
    </div>
  );
}

export const setHintId = (id: string) => `${id}-hint`;

export function SetField({
  id,
  label,
  hint,
  children,
  wide = false,
}: {
  id: string;
  label: ReactNode;
  hint?: ReactNode;
  children: ReactNode;
  wide?: boolean;
}) {
  return (
    <div className={`field set-field ${wide ? "set-field--wide" : ""}`}>
      <label className="field__label" htmlFor={id}>
        {label}
      </label>
      {children}
      {hint && (
        <span id={setHintId(id)} className="set-hint">
          {hint}
        </span>
      )}
    </div>
  );
}

/**
 * Ввод секрета. Это обычное текстовое поле, замаскированное CSS: на type="password"
 * менеджер паролей браузера сам подставляет сохранённый пароль — страница видит «правку»,
 * которую никто не делал, и спрашивает «Уйти без сохранения?».
 */
export function SetSecretInput({
  id,
  value,
  onChange,
  placeholder,
  labelledBy,
  autoFocus,
}: {
  id: string;
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  labelledBy?: string;
  autoFocus?: boolean;
}) {
  const [show, setShow] = useState(false);
  return (
    <div className="set-secret">
      <input
        id={id}
        name={`set-${id}`}
        className={`input set-secret__input ${show ? "" : "is-masked"}`}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="off"
        spellCheck={false}
        autoFocus={autoFocus}
        data-lpignore="true"
        data-1p-ignore=""
        data-bwignore=""
        data-form-type="other"
        aria-labelledby={labelledBy}
      />
      <button
        type="button"
        className="icon-btn set-secret__eye"
        onClick={() => setShow((v) => !v)}
        aria-label={show ? "Скрыть введённое" : "Показать введённое"}
        aria-pressed={show}
        title={show ? "Скрыть" : "Показать"}
      >
        {show ? <EyeOff size={18} strokeWidth={1.9} /> : <Eye size={18} strokeWidth={1.9} />}
      </button>
    </div>
  );
}

/**
 * Карточка ключа. Сохранён — крупно «Сохранён ••••1234», «Заменить» и «Удалить».
 * Не сохранён — поле и «Сохранить»; после сохранения карточка сразу показывает маску.
 */
export function SetKey({
  id,
  title,
  isSet,
  mask,
  locked,
  envKey,
  placeholder,
  onSave,
  onRemove,
  validate,
  details,
  help,
}: {
  id: string;
  title: string;
  isSet: boolean;
  mask: string;
  locked: boolean;
  envKey: string;
  placeholder: string;
  /** бросает ошибку, если не сохранилось */
  onSave: (v: string) => Promise<unknown>;
  /** false — пользователь передумал */
  onRemove?: () => Promise<boolean>;
  validate?: (v: string) => string;
  /** под строкой «Сохранён» — например, права ключа */
  details?: ReactNode;
  /** рядом с «Сохранить», когда ключа нет */
  help?: ReactNode;
}) {
  const titleId = useId();
  const [replacing, setReplacing] = useState(false);
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState<"save" | "del" | null>(null);
  const [error, setError] = useState("");
  const editing = !locked && (!isSet || replacing);
  useSetDirty(`key-${id}`, editing && value.trim() !== "");

  const save = async (e: FormEvent) => {
    e.preventDefault();
    const v = value.trim();
    if (!v || busy) return;
    const bad = validate?.(v);
    if (bad) return setError(bad);
    setBusy("save");
    setError("");
    try {
      await onSave(v);
      setValue("");
      setReplacing(false);
    } catch (err) {
      setError(errText(err));
    } finally {
      setBusy(null);
    }
  };
  const remove = async () => {
    if (!onRemove || busy) return;
    setBusy("del");
    setError("");
    try {
      await onRemove();
    } catch (err) {
      setError(errText(err));
    } finally {
      setBusy(null);
    }
  };
  const cancel = () => {
    setReplacing(false);
    setValue("");
    setError("");
  };

  const head = (
    <h3 id={titleId} className="set-card__title">
      {title}
    </h3>
  );
  const errorLine = error && (
    <div className="error-line" role="alert">
      <CircleAlert size={16} aria-hidden="true" />
      <span>{error}</span>
    </div>
  );

  if (!editing) {
    return (
      <div className={`set-card set-card--${isSet ? "ok" : "idle"}`} aria-labelledby={titleId} role="group">
        {head}
        <div className="set-card__state">
          <span className="set-card__icon" aria-hidden="true">
            {locked ? <Lock size={20} strokeWidth={2} /> : <CircleCheck size={20} strokeWidth={2} />}
          </span>
          <div className="set-card__text">
            <p className="set-card__main">
              {isSet ? "Сохранён" : "Не задан"} {mask && <span className="set-card__mask">{mask}</span>}
            </p>
            {locked && <p className="set-card__sub">{setEnvHint(envKey)}</p>}
            {details}
          </div>
        </div>
        {errorLine}
        {!locked && (
          <div className="set-card__actions">
            <button type="button" className="btn btn--small" onClick={() => setReplacing(true)} disabled={!!busy}>
              Заменить
            </button>
            {onRemove && (
              <button type="button" className="btn btn--small btn--ghost btn--danger" onClick={() => void remove()} disabled={!!busy}>
                {busy === "del" ? "Удаляю…" : "Удалить"}
              </button>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <form className="set-card set-card--idle" onSubmit={save} noValidate aria-labelledby={titleId} autoComplete="off">
      {head}
      <div className="set-card__state">
        <span className="set-card__icon" aria-hidden="true">
          <KeyRound size={20} strokeWidth={2} />
        </span>
        <div className="set-card__text">
          <p className="set-card__main">{replacing ? `Сейчас ${mask}` : "Не сохранён"}</p>
        </div>
      </div>
      <SetSecretInput
        id={id}
        value={value}
        onChange={(v) => {
          setValue(v);
          if (error) setError("");
        }}
        placeholder={placeholder}
        labelledBy={titleId}
        autoFocus={replacing}
      />
      {errorLine}
      <div className="set-card__actions">
        <button type="submit" className={`btn btn--small ${value.trim() ? "btn--primary" : ""}`} disabled={!value.trim() || !!busy}>
          {busy === "save" ? "Сохраняю…" : "Сохранить"}
        </button>
        {replacing && (
          <button type="button" className="btn btn--small btn--ghost" onClick={cancel} disabled={!!busy}>
            Отмена
          </button>
        )}
        {help && <span className="set-card__help">{help}</span>}
      </div>
    </form>
  );
}

/** Кнопка «Сохранить» загорается красным, только когда есть что сохранять. */
export function SetSaveBar({
  dirty,
  busy,
  onReset,
  error,
  label = "Сохранить",
  disabled = false,
}: {
  dirty: boolean;
  busy: boolean;
  onReset: () => void;
  error?: string;
  label?: string;
  disabled?: boolean;
}) {
  return (
    <div className="set-save">
      {error && (
        <div className="error-line set-save__error" role="alert">
          <CircleAlert size={16} aria-hidden="true" />
          <span>{error}</span>
        </div>
      )}
      <div className="set-save__row">
        <button type="submit" className={`btn ${dirty ? "btn--primary" : ""}`} disabled={!dirty || busy || disabled}>
          {busy ? "Сохраняю…" : label}
        </button>
        {dirty && !busy && (
          <>
            <button type="button" className="btn btn--ghost" onClick={onReset}>
              Отменить
            </button>
            <span className="set-save__note">Не сохранено</span>
          </>
        )}
      </div>
    </div>
  );
}

/** Пары «название — значение». */
export function SetFacts({ items }: { items: [ReactNode, ReactNode][] }) {
  return (
    <dl className="set-facts">
      {items.map(([k, v], i) => (
        <div key={i} className="set-facts__row">
          <dt>{k}</dt>
          <dd>{v}</dd>
        </div>
      ))}
    </dl>
  );
}

/** «4,2 КБ» */
export function setSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} Б`;
  return `${(bytes / 1024).toFixed(bytes < 10240 ? 1 : 0).replace(".", ",")} КБ`;
}

/** Плавная прокрутка с учётом prefers-reduced-motion. */
export function setScrollTo(el: Element | null, block: ScrollLogicalPosition = "start") {
  if (!el) return;
  const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  el.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block });
}
